import { LOCAL_DRACO_DECODER_ASSETS } from "../components/3d/loaders/assets/draco.constants";
import {
  resolveArtworkRuntimeAssetUrls,
} from "../components/3d/loaders/artwork/artwork.runtime.assets";
import { ArtworkFrame } from "../components/3d/loaders/artwork/attachments/frame.attachment";
import {
  ARROW_IMAGE_URL,
  DEFAULT_ARTWORK_MODEL_IMAGE_URL,
  DEFAULT_ARTWORK_PRELOADER,
} from "../components/3d/loaders/artwork/artwork.constants";
import { getRenderPassAssetUrls } from "../components/3d/renderer/services/render.pass.assets";
import { getModelLoadSourceUrls } from "../components/3d/utils/general.utils";
import { Artwork, ArtworkWrapper } from "../types/artworksData";
import { Gallery } from "../types/galleryData";
import { SpaceData } from "../types/spaceData";
import { getSpaceMoodAssetUrls } from "../components/3d/loaders/space/space.runtime.assets";
import { getProxiedImageUrl, getProxiedSoundUrl } from "../utils/imageProxy";
import {
  OfflineGalleryAsset,
  OfflineGalleryManifest,
} from "./offline.types";

type BuildOfflineGalleryManifestOptions = {
  baseApi?: string;
  gallery: Gallery;
  routePath: string;
  space: SpaceData;
};

const isPublicGalleryRoutePath = (routePath: string, galleryId: string): boolean => {
  try {
    const routeUrl = new URL(routePath, window.location.origin);
    const routeMode = routeUrl.searchParams.get("t");
    return routeUrl.pathname === `/gallery/${galleryId}` && routeMode !== "personal" && routeMode !== "admin";
  } catch (_error) {
    return routePath === `/gallery/${galleryId}`;
  }
};

const toAbsoluteUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  try {
    return new URL(url, window.location.origin).toString();
  } catch (_error) {
    return null;
  }
};

const joinApiUrl = (baseApi: string, path: string): string | null => {
  const normalizedBase = baseApi || window.location.origin;
  try {
    return new URL(path, normalizedBase.endsWith("/") ? normalizedBase : `${normalizedBase}/`).toString();
  } catch (_error) {
    return null;
  }
};

const resolveApiOrigin = (baseApi: string): string => {
  try {
    return new URL(baseApi || window.location.origin, window.location.origin).origin;
  } catch (_error) {
    return window.location.origin;
  }
};

const pushAsset = (
  assets: OfflineGalleryAsset[],
  url: string | null | undefined,
  kind: OfflineGalleryAsset["kind"],
  label: string
) => {
  const absoluteUrl = toAbsoluteUrl(url);
  if (!absoluteUrl) return;

  assets.push({
    url: absoluteUrl,
    kind,
    label,
    required: true,
  });
};

const toRuntimeAssetUrl = (
  asset: { url?: string | null; kind: OfflineGalleryAsset["kind"] },
  baseApi: string
): string | null | undefined => {
  if (asset.kind === "texture") {
    return asset.url ? getProxiedImageUrl(asset.url, baseApi) : asset.url;
  }

  return asset.url;
};

const collectArtworkAssets = (
  artworkWrappers: Array<ArtworkWrapper | null>,
  baseApi: string,
  includeAllWrapperArtworks: boolean
): OfflineGalleryAsset[] => {
  const assets: OfflineGalleryAsset[] = [];

  artworkWrappers.forEach((wrapper) => {
    if (!wrapper?.artworks?.length) return;

    const artworksToCache = includeAllWrapperArtworks
      ? wrapper.artworks
      : [wrapper.artworks[wrapper.activeArtwork]];

    artworksToCache.forEach((artwork: Artwork | null | undefined, artworkIndex) => {
      if (!artwork?.artworkData) return;

      const frame = artwork.artworkData.frame || {
        type: "default",
        artworkWrapperID: wrapper._id,
        color: "#000000",
        thickness: 1,
        pasPartoutSize: 0.1,
      };
      const frameUrl = ArtworkFrame.getFrameUrl(frame.type);
      const runtimeAssets = resolveArtworkRuntimeAssetUrls(artwork, frameUrl);

      getModelLoadSourceUrls(runtimeAssets.modelSource).forEach((url, modelIndex) => {
        pushAsset(assets, url, "model", `artwork-model-${artwork._id}-${artworkIndex}-${modelIndex}`);
      });

      const displayUrl = runtimeAssets.displayUrl && runtimeAssets.displayKind !== "video"
        ? getProxiedImageUrl(runtimeAssets.displayUrl, baseApi)
        : runtimeAssets.displayUrl;
      const soundUrl = runtimeAssets.soundUrl
        ? getProxiedSoundUrl(runtimeAssets.soundUrl, baseApi)
        : null;

      pushAsset(assets, displayUrl, runtimeAssets.displayKind, `artwork-display-${artwork._id}`);
      pushAsset(assets, soundUrl, "audio", `artwork-sound-${artwork._id}`);
    });
  });

  return assets;
};

export const buildOfflineGalleryManifest = ({
  baseApi,
  gallery,
  routePath,
  space,
}: BuildOfflineGalleryManifestOptions): OfflineGalleryManifest => {
  if (!isPublicGalleryRoutePath(routePath, gallery._id)) {
    throw new Error("Only public gallery routes can be prepared for offline replay.");
  }

  const resolvedBaseApi = baseApi || process.env.REACT_APP_BASE_API || "";
  const activeMoodIndex = Number.isInteger(gallery?.data?.mood) ? gallery.data.mood : 0;
  const activeMood = space?.data?.moods?.[activeMoodIndex] || space?.data?.moods?.[0];
  const assets: OfflineGalleryAsset[] = [];
  const isPseudoMoMSpace = space?.type === "custom" && space?.subtype === "pseudoMoM";

  [
    ARROW_IMAGE_URL,
    DEFAULT_ARTWORK_MODEL_IMAGE_URL,
    DEFAULT_ARTWORK_PRELOADER,
  ].forEach((assetUrl) => {
    pushAsset(assets, assetUrl, "app-shell", "local-gallery-playback");
  });

  getSpaceMoodAssetUrls(space, activeMood).forEach((asset) => {
    pushAsset(assets, toRuntimeAssetUrl(asset, resolvedBaseApi), asset.kind, asset.label);
  });

  getRenderPassAssetUrls(activeMood?.renderSettings).forEach((asset) => {
    pushAsset(assets, asset.url, asset.kind, asset.label);
  });

  collectArtworkAssets(gallery?.artworkWrappers || [], resolvedBaseApi, isPseudoMoMSpace).forEach((asset) => {
    pushAsset(assets, asset.url, asset.kind, asset.label || "artwork-asset");
  });

  pushAsset(assets, gallery?.data?.sound?.url, "audio", "background-sound");
  pushAsset(assets, gallery?.data?.pseudoMomIntroSound?.url, "audio", "pseudo-mom-intro-sound");
  LOCAL_DRACO_DECODER_ASSETS.forEach((decoderAsset) => {
    pushAsset(assets, decoderAsset, "draco", "draco-decoder");
  });

  const jsonUrls = [
    joinApiUrl(resolvedBaseApi, `/gallery/${gallery._id}`),
    joinApiUrl(resolvedBaseApi, `/space/${gallery.spaceId}`),
  ].filter(Boolean) as string[];

  const dedupedAssets = Array.from(new Map(assets.map((asset) => [asset.url, asset])).values());

  return {
    galleryId: gallery._id,
    title: (gallery as any).title || gallery.name || "Public gallery",
    routePath,
    preparedAt: new Date().toISOString(),
    apiOrigin: resolveApiOrigin(resolvedBaseApi),
    jsonUrls,
    appShellUrls: [],
    assets: dedupedAssets,
  };
};
